import { Box, Button, CircularProgress, Grid, makeStyles, Paper, Typography } from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";
import { useEffect, useState } from "react";
import CopyIcon from "src/components/Icon/CopyIcon";
import { THEME_MODE } from "src/config/constants";
import useNotify from "src/hooks/useNotify";
import { mergeClient } from "src/services/requestClient";
import { formatAddress } from "src/services/utils";
import { useWeb3Connect } from "src/wallet-connection/context/we3-connect-context";

const useStyle = makeStyles((theme) => ({
  addressItem: {
    padding: "0.25rem 0.25rem 0.25rem 0.5rem",
    margin: "0rem 0.5rem 0.25rem 0rem",
    borderRadius: "6px",
    cursor: "pointer",
    background: theme.palette.background.morePaper,
  },
  copyIcon: {
    fontSize: "14px",
  },
  clearIcon: {
    fontSize: "16px",
    cursor: "pointer",
    color: theme.palette.text.secondary,
  },
  confirmBut: {
    width: "170px",
    marginRight: "1rem",
  },
  mergeCreditScoreText: {
    color: theme.palette.type === THEME_MODE.DARK ? "#7994C1" : "#8695A7",
  },
  mergeCreditScoreFigure: {
    fontSize: "40px",
    fontWeight: 550,
  },
  divide: {
    height: "100%",
    width: "1px",
    background: theme.palette.text.secondary,
    marginRight: "0.5rem",
  },
}));

export default function ResultPaper({ newAddress, setNewAddress, ...props }) {
  const cls = useStyle();
  const [addresses, setAddresses] = useState({});
  const [mergeCreditScore, setMergeCreditScore] = useState(null);
  const { warnNotify } = useNotify();
  const { address } = useWeb3Connect();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (address) {
      let lowAddress = address.toLowerCase();
      let temp = {};
      temp[lowAddress] = {
        address: lowAddress,
        credit_score: null,
      };
      setAddresses(temp);
    }
  }, [address]);

  useEffect(() => {
    if (newAddress) {
      const lowAddress = newAddress.toLowerCase();
      if (lowAddress.length > 0) {
        if (addresses[lowAddress]) warnNotify("Wallet address already added");
        else {
          const _temp = { ...addresses };
          _temp[lowAddress] = {
            address: lowAddress,
            credit_score: null,
          };
          setAddresses(_temp);
          setNewAddress(null);
        }
      }
    }
  }, [newAddress]);

  const onMergeAccountClick = async () => {
    try {
      setLoading(true);
      const data = await mergeClient.post("/merger", Object.keys(addresses));
      const _data = data.data;
      setMergeCreditScore(_data["credit_score_merge_wallet"]);
      const _walletData = _data.wallet;
      let _tempData = addresses;
      for (var _wallet of _walletData) {
        _tempData[_wallet["address"]]["credit_score"] = _wallet["credit_score"];
      }
      setAddresses({ ..._tempData });
      setError(null);
    } catch (error) {
      console.error(error);
      setError("Load fail");
    } finally {
      setLoading(false);
    }
  };

  const onRemoveAddressClick = (address) => {
    let _tempData = addresses;
    delete _tempData[address];
    setAddresses({ ..._tempData });
  };

  return (
    <>
      {Object.keys(addresses).length > 0 && (
        <Grid container spacing={2} {...props}>
          <Grid item md={9} xs={12}>
            <Paper>
              <Grid container>
                <Grid item xs={11}>
                  <Box display="flex" flexWrap="wrap">
                    {Object.keys(addresses).map((address, index) => {
                      const data = addresses[address];
                      return (
                        <Box
                          display="inline-flex"
                          justifyContent="center"
                          alignItems="center"
                          key={index}
                          className={cls.addressItem}
                        >
                          <Typography color="textSecondary">{formatAddress(address, 5)}</Typography>
                          {data.credit_score && (
                            <Typography color="secondary" variant="h4" style={{ marginLeft: "0.5rem" }}>
                              {data.credit_score}
                            </Typography>
                          )}
                          <CopyIcon
                            defaultText="Copy address"
                            copyText={address}
                            successText="Copied address!"
                            classes={{ icon: cls.copyIcon }}
                          />
                          <ClearIcon className={cls.clearIcon} onClick={() => onRemoveAddressClick(address)} />
                        </Box>
                      );
                    })}
                  </Box>
                  <Box display="flex" justifyContent="flex-start" alignItems="center" mt={2}>
                    <Button
                      color="secondary"
                      variant="contained"
                      onClick={() => onMergeAccountClick()}
                      className={cls.confirmBut}
                      disabled={loading}
                    >
                      {loading ? (
                        <CircularProgress size={25} color="#FFFFFF" />
                      ) : (
                        <Typography>Confirm ownership</Typography>
                      )}
                    </Button>
                    {error && (
                      <Typography color="error" variant="subtitle2">
                        {error}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={1}>
                  <Box display="flex" justifyContent="center" alignItems="flex-start" height="100%">
                    <div className={cls.divide}></div>
                    <ClearIcon
                      className={cls.clearIcon}
                      style={{ fontSize: "28px" }}
                      onClick={() => setAddresses({})}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item md={3} xs={12}>
            <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" height="100%">
              <Typography variant="h6" className={cls.mergeCreditScoreText}>
                MERGED CREDIT SCORE
              </Typography>
              {mergeCreditScore && (
                <Typography color="primary" className={cls.mergeCreditScoreFigure}>
                  {mergeCreditScore}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      )}
    </>
  );
}
